import { createContext, useContext, useMemo, type ReactNode } from 'react';
import { useLiveQuery } from 'dexie-react-hooks';
import { can as canDo, type Actor, type Capability } from '@pos/core';
import {
  getMeta,
  type BusinessRow,
  type DeviceIdentity,
  type DeviceRow,
  type LocalSession,
  type MembershipRow,
  type WriteActor,
} from '@pos/local-db';
import { db } from '../lib/db.ts';

export interface CurrentState {
  device?: DeviceIdentity;
  deviceRow?: DeviceRow;
  session?: LocalSession;
  business?: BusinessRow;
  membership?: MembershipRow;
}

/** Estado del dispositivo y la sesión, en vivo desde la base local. undefined = cargando. */
export function useCurrentState(): CurrentState | undefined {
  return useLiveQuery(async () => {
    const device = await getMeta(db, 'device');
    const session = await getMeta(db, 'session');
    const [business, deviceRow, membership] = await Promise.all([
      device ? db.businesses.get(device.business_id) : undefined,
      device ? db.devices.get(device.id) : undefined,
      session ? db.memberships.get(session.membership_id) : undefined,
    ]);
    return { device, deviceRow, session, business, membership };
  });
}

export interface SessionContextValue {
  device: DeviceIdentity;
  session: LocalSession;
  business: BusinessRow;
  membership: MembershipRow;
  /** Autor de las escrituras locales (va a la cola de sincronización). */
  writer: WriteActor;
  can: (capability: Capability) => boolean;
  isOwner: boolean;
}

const SessionContext = createContext<SessionContextValue | null>(null);

export function SessionProvider({
  value,
  children,
}: {
  value: Omit<SessionContextValue, 'writer' | 'can' | 'isOwner'>;
  children: ReactNode;
}) {
  const { device, session, business, membership } = value;
  const full = useMemo<SessionContextValue>(() => {
    const actor: Actor = { role: membership.role, permissions: membership.permissions, active: membership.active };
    return {
      device,
      session,
      business,
      membership,
      writer: { user_id: membership.user_id, business_id: business.id, device_id: device.id },
      can: (capability) => canDo(actor, capability),
      isOwner: membership.role === 'owner',
    };
  }, [device, session, business, membership]);
  return <SessionContext.Provider value={full}>{children}</SessionContext.Provider>;
}

export function useSession(): SessionContextValue {
  const value = useContext(SessionContext);
  if (!value) throw new Error('useSession fuera de una sesión activa');
  return value;
}
